import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import {
  Loader2, Search, ShieldAlert, CheckCircle2, Clock, ChevronRight,
  FileSpreadsheet, FileText, Activity, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

import { SiteHeader } from "@/components/site/SiteHeader";
import { AdminNav } from "@/components/admin/AdminNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  listStripeWebhookEvents,
  getStripeWebhookEventDetail,
  getStripeWebhookStats,
  exportStripeWebhookEvents,
  type StripeWebhookEventsFilters,
  type StripeWebhookEventsCursor,
  type StripeWebhookEventRow,
} from "@/lib/stripe-webhook-events.functions";
import { formatStripeAmount } from "@/lib/stripe-event-summary";
import { CATEGORY_LABELS, type StripeEventCategory } from "@/lib/stripe-event-category";
// PERF: el módulo de exportación carga xlsx/pdf-lib bajo demanda (import
// dinámico dentro de cada exportador), así la ruta no arrastra ~870 kB.
import { exportEventsToExcel, exportEventsToPdf } from "@/lib/stripe-export";

export const Route = createFileRoute("/_authenticated/admin/stripe-events")({
  head: () => ({
    meta: [
      { title: "Admin · Stripe webhook events — Mostrando México" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: StripeEventsPage,
});

type CategoryFilter = StripeEventCategory | "all";

type FormState = {
  from: string;
  to: string;
  env: "" | "sandbox" | "live";
  eventType: string;
  status: "all" | "processed" | "pending";
  eventId: string;
  outcome: "all" | "success" | "failure" | "pending" | "neutral";
  search: string;
  currency: string;
  customer: string;
  customerEmail: string;
  paymentIntentId: string;
  chargeId: string;
  sessionId: string;
  amountMin: string;
  amountMax: string;
};

const PAGE_SIZE = 50;

const EMPTY: FormState = {
  from: "", to: "", env: "", eventType: "", status: "all", eventId: "",
  outcome: "all", search: "", currency: "", customer: "", customerEmail: "",
  paymentIntentId: "", chargeId: "", sessionId: "", amountMin: "", amountMax: "",
};

type AppliedFilters = Omit<StripeWebhookEventsFilters, "cursor">;

const DEFAULT_APPLIED: AppliedFilters = {
  status: "all",
  category: "all",
  outcome: "all",
  pageSize: PAGE_SIZE,
};

const CATEGORY_TABS: CategoryFilter[] = [
  "all", "payments", "checkout", "refunds", "disputes", "customers",
  "subscriptions", "invoices", "payouts", "transfers", "fraud", "other",
];

function categoryTabLabel(c: CategoryFilter): string {
  return c === "all" ? "Todos" : CATEGORY_LABELS[c];
}

function toFilters(f: FormState, category: CategoryFilter): AppliedFilters {
  const iso = (v: string) => (v ? new Date(v).toISOString() : undefined);
  const txt = (v: string) => v.trim() || undefined;
  const num = (v: string) => {
    const n = Number(v.replace(",", "."));
    return v.trim() && Number.isFinite(n) && n >= 0 ? n : undefined;
  };
  return {
    from: iso(f.from),
    to: iso(f.to),
    env: f.env || undefined,
    eventType: txt(f.eventType),
    status: f.status,
    eventId: txt(f.eventId),
    category,
    outcome: f.outcome,
    search: txt(f.search),
    currency: txt(f.currency),
    customer: txt(f.customer),
    customerEmail: txt(f.customerEmail),
    paymentIntentId: txt(f.paymentIntentId),
    chargeId: txt(f.chargeId),
    sessionId: txt(f.sessionId),
    amountMin: num(f.amountMin),
    amountMax: num(f.amountMax),
    pageSize: PAGE_SIZE,
  };
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "medium" });
}

function outcomeBadge(row: StripeWebhookEventRow) {
  const label = row.status ?? row.outcome ?? "—";
  const cls =
    row.outcome === "success" ? "bg-emerald-500/15 text-emerald-700"
      : row.outcome === "failure" ? "bg-destructive/15 text-destructive"
        : row.outcome === "pending" ? "bg-amber-500/15 text-amber-700"
          : "bg-muted text-muted-foreground";
  return <span className={`rounded px-2 py-0.5 text-xs font-medium ${cls}`}>{label}</span>;
}

function StripeEventsPage() {
  const list = useServerFn(listStripeWebhookEvents);
  const detailFn = useServerFn(getStripeWebhookEventDetail);
  const statsFn = useServerFn(getStripeWebhookStats);
  const exportFn = useServerFn(exportStripeWebhookEvents);

  const [form, setForm] = useState<FormState>(EMPTY);
  const [category, setCategory] = useState<CategoryFilter>("all");
  const [applied, setApplied] = useState<AppliedFilters>(DEFAULT_APPLIED);
  const [openId, setOpenId] = useState<string | null>(null);
  const [exporting, setExporting] = useState<"" | "xlsx" | "pdf">("");

  const query = useInfiniteQuery({
    queryKey: ["stripe-webhook-events", applied],
    initialPageParam: null as StripeWebhookEventsCursor | null,
    queryFn: ({ pageParam }) => list({ data: { ...applied, cursor: pageParam ?? undefined } }),
    getNextPageParam: (last) => last.nextCursor,
    retry: false,
  });

  const stats = useQuery({
    queryKey: ["stripe-webhook-stats", applied],
    queryFn: () => statsFn({ data: applied }),
    retry: false,
  });

  const detail = useQuery({
    queryKey: ["stripe-webhook-event-detail", openId],
    queryFn: () => detailFn({ data: { eventId: openId! } }),
    enabled: !!openId,
    retry: false,
  });

  const onApply = (e: React.FormEvent) => {
    e.preventDefault();
    setApplied(toFilters(form, category));
  };

  const onCategory = (c: CategoryFilter) => {
    setCategory(c);
    setApplied(toFilters(form, c));
  };

  const runExport = async (kind: "xlsx" | "pdf") => {
    setExporting(kind);
    try {
      const { rows: all, truncated } = await exportFn({ data: applied });
      if (all.length === 0) {
        toast.info("No hay eventos para exportar con los filtros actuales.");
        return;
      }
      if (kind === "xlsx") {
        await exportEventsToExcel(all, { from: applied.from, to: applied.to });
      } else {
        await exportEventsToPdf(
          all,
          {
            total: stats.data?.total ?? all.length,
            succeeded: stats.data?.succeeded ?? 0,
            failed: stats.data?.failed ?? 0,
            refunds: stats.data?.refunds ?? 0,
            disputes: stats.data?.disputes ?? 0,
            amountByCurrency: stats.data?.amountByCurrency ?? [],
          },
          { from: applied.from, to: applied.to },
        );
      }
      toast.success(
        truncated
          ? `Exportados los primeros ${all.length} eventos (filtra por fechas para el resto).`
          : `Exportados ${all.length} eventos.`,
      );
    } catch (err) {
      toast.error((err as Error).message || "No se pudo exportar.");
    } finally {
      setExporting("");
    }
  };

  const rows = useMemo(
    () => query.data?.pages.flatMap((p) => p.rows) ?? [],
    [query.data],
  );
  const forbidden =
    query.error instanceof Error && /forbidden|unauthorized/i.test(query.error.message);

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="container mx-auto max-w-7xl space-y-6 px-4 py-8">
        <div className="space-y-3">
          <AdminNav />
          <h1 className="text-2xl font-semibold">Stripe · Eventos del webhook</h1>
          <p className="text-sm text-muted-foreground">
            Todos los eventos recibidos (pagos, checkout, cargos, reembolsos, clientes, suscripciones,
            facturas, transferencias, disputas y fraude). Estado <em>reservado</em> = fila creada pero handler
            aún no finaliza. <em>Procesado</em> = handler ejecutado con éxito.
          </p>
        </div>

        {forbidden ? (
          <div className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
            <ShieldAlert className="h-4 w-4" /> No tienes permisos para ver esta sección.
          </div>
        ) : (
          <>
            {/* Estado del webhook + métricas */}
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-lg border bg-card p-4">
                <div className="flex items-center gap-2 text-sm font-medium">
                  {stats.data?.healthy ? (
                    <>
                      <span className="h-2 w-2 rounded-full bg-emerald-500" />
                      Webhook conectado
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                      Sin eventos recientes
                    </>
                  )}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {stats.data?.last
                    ? `Último: ${stats.data.last.event_type ?? "—"} · ${fmtDate(stats.data.last.received_at)} (${stats.data.last.env})`
                    : "Aún no se ha recibido ningún evento."}
                </p>
              </div>
              <div className="rounded-lg border bg-card p-4">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Activity className="h-4 w-4" /> Eventos (filtro actual)
                </div>
                <p className="mt-1 text-2xl font-semibold">{stats.data?.total ?? "—"}</p>
                <p className="text-xs text-muted-foreground">
                  Hoy: {stats.data?.today ?? 0} · Pendientes: {stats.data?.pending ?? 0}
                </p>
              </div>
              <div className="rounded-lg border bg-card p-4">
                <div className="text-sm font-medium">Resultados (eventos)</div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Éxito: {stats.data?.succeeded ?? 0} · Fallidos: {stats.data?.failed ?? 0}
                  <br />
                  Reembolsos: {stats.data?.refunds ?? 0} · Disputas: {stats.data?.disputes ?? 0}
                  <br />
                  Pagos únicos: {stats.data?.uniquePayments ?? 0}
                </p>
              </div>
              <div className="rounded-lg border bg-card p-4">
                <div className="text-sm font-medium">Montos</div>
                <div className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                  {(stats.data?.amountByCurrency ?? []).length === 0 ? (
                    <span>—</span>
                  ) : (
                    stats.data!.amountByCurrency.map((c) => (
                      <div key={c.currency}>
                        {c.currency}: {formatStripeAmount(c.received, c.currency)}
                        {c.refunded ? ` · reemb. ${formatStripeAmount(c.refunded, c.currency)}` : ""}
                      </div>
                    ))
                  )}
                  <div className="pt-1 text-[11px] opacity-80">
                    Sin duplicar: un pago se suma una sola vez aunque genere varios eventos.
                  </div>
                </div>
              </div>
            </div>

            {/* Selector de categorías */}
            <div className="flex flex-wrap gap-2">
              {CATEGORY_TABS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => onCategory(c)}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                    category === c
                      ? "border-primary bg-primary text-primary-foreground"
                      : "bg-card hover:bg-accent"
                  }`}
                >
                  {categoryTabLabel(c)}
                </button>
              ))}
            </div>

            <form
              onSubmit={onApply}
              className="grid grid-cols-1 gap-3 rounded-lg border bg-card p-4 md:grid-cols-3"
            >
              <div className="space-y-1 md:col-span-3">
                <Label htmlFor="search">Buscador (tipo, cliente, correo, IDs, descripción)</Label>
                <Input
                  id="search" placeholder="cozumel@correo.com, pi_3Q…, checkout.session"
                  value={form.search}
                  onChange={(e) => setForm({ ...form, search: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="from">Desde</Label>
                <Input
                  id="from" type="datetime-local" value={form.from}
                  onChange={(e) => setForm({ ...form, from: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="to">Hasta</Label>
                <Input
                  id="to" type="datetime-local" value={form.to}
                  onChange={(e) => setForm({ ...form, to: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label>Ambiente</Label>
                <Select
                  value={form.env || "all"}
                  onValueChange={(v) =>
                    setForm({ ...form, env: v === "all" ? "" : (v as "sandbox" | "live") })
                  }
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="sandbox">Sandbox</SelectItem>
                    <SelectItem value="live">Live</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="eventType">Tipo (contiene)</Label>
                <Input
                  id="eventType" placeholder="checkout.session.completed"
                  value={form.eventType}
                  onChange={(e) => setForm({ ...form, eventType: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label>Estado del handler</Label>
                <Select
                  value={form.status}
                  onValueChange={(v) => setForm({ ...form, status: v as FormState["status"] })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="processed">Procesados</SelectItem>
                    <SelectItem value="pending">Reservados (pendientes)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Resultado</Label>
                <Select
                  value={form.outcome}
                  onValueChange={(v) => setForm({ ...form, outcome: v as FormState["outcome"] })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="success">Éxito</SelectItem>
                    <SelectItem value="failure">Fallido</SelectItem>
                    <SelectItem value="pending">Pendiente</SelectItem>
                    <SelectItem value="neutral">Informativo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="customer">Cliente (nombre)</Label>
                <Input
                  id="customer" value={form.customer}
                  onChange={(e) => setForm({ ...form, customer: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="customerEmail">Correo</Label>
                <Input
                  id="customerEmail" value={form.customerEmail}
                  onChange={(e) => setForm({ ...form, customerEmail: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="currency">Moneda</Label>
                <Input
                  id="currency" placeholder="mxn / usd"
                  value={form.currency}
                  onChange={(e) => setForm({ ...form, currency: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="amountMin">Monto mínimo</Label>
                <Input
                  id="amountMin" inputMode="decimal" placeholder="0"
                  value={form.amountMin}
                  onChange={(e) => setForm({ ...form, amountMin: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="amountMax">Monto máximo</Label>
                <Input
                  id="amountMax" inputMode="decimal" placeholder="10000"
                  value={form.amountMax}
                  onChange={(e) => setForm({ ...form, amountMax: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="eventId">Event ID</Label>
                <Input
                  id="eventId" placeholder="evt_..."
                  value={form.eventId}
                  onChange={(e) => setForm({ ...form, eventId: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="paymentIntentId">Payment Intent ID</Label>
                <Input
                  id="paymentIntentId" placeholder="pi_..."
                  value={form.paymentIntentId}
                  onChange={(e) => setForm({ ...form, paymentIntentId: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="chargeId">Charge ID</Label>
                <Input
                  id="chargeId" placeholder="ch_..."
                  value={form.chargeId}
                  onChange={(e) => setForm({ ...form, chargeId: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="sessionId">Checkout Session ID</Label>
                <Input
                  id="sessionId" placeholder="cs_..."
                  value={form.sessionId}
                  onChange={(e) => setForm({ ...form, sessionId: e.target.value })}
                />
              </div>
              <div className="flex flex-wrap items-end gap-2 md:col-span-3">
                <Button type="submit" disabled={query.isFetching}>
                  {query.isFetching
                    ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    : <Search className="mr-2 h-4 w-4" />}
                  Aplicar filtros
                </Button>
                <Button
                  type="button" variant="outline"
                  onClick={() => {
                    setForm(EMPTY);
                    setCategory("all");
                    setApplied(DEFAULT_APPLIED);
                  }}
                >
                  Limpiar
                </Button>
                <div className="ml-auto flex gap-2">
                  <Button
                    type="button" variant="outline"
                    onClick={() => void runExport("xlsx")}
                    disabled={exporting !== ""}
                  >
                    {exporting === "xlsx"
                      ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      : <FileSpreadsheet className="mr-2 h-4 w-4" />}
                    Exportar Excel
                  </Button>
                  <Button
                    type="button" variant="outline"
                    onClick={() => void runExport("pdf")}
                    disabled={exporting !== ""}
                  >
                    {exporting === "pdf"
                      ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      : <FileText className="mr-2 h-4 w-4" />}
                    Exportar PDF
                  </Button>
                </div>
              </div>
            </form>

            <div className="rounded-lg border bg-card">
              <div className="flex items-center justify-between border-b px-4 py-2 text-sm text-muted-foreground">
                <span>
                  {rows.length} cargado(s) · página {PAGE_SIZE}
                  {query.hasNextPage ? " · hay más" : ""}
                </span>
                {query.isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-left text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2">Fecha</th>
                      <th className="px-3 py-2">Tipo</th>
                      <th className="px-3 py-2">Categoría</th>
                      <th className="px-3 py-2">Estado</th>
                      <th className="px-3 py-2">Cliente</th>
                      <th className="px-3 py-2">Correo</th>
                      <th className="px-3 py-2">Monto</th>
                      <th className="px-3 py-2">Payment ID</th>
                      <th className="px-3 py-2">Env</th>
                      <th className="px-3 py-2">Handler</th>
                      <th className="px-3 py-2 w-8" />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr
                        key={r.stripe_event_id}
                        className="cursor-pointer border-t hover:bg-accent/40"
                        onClick={() => setOpenId(r.stripe_event_id)}
                      >
                        <td className="whitespace-nowrap px-3 py-2">{fmtDate(r.received_at)}</td>
                        <td className="px-3 py-2">{r.event_type ?? "—"}</td>
                        <td className="px-3 py-2 text-xs">
                          {CATEGORY_LABELS[(r.category ?? "other") as StripeEventCategory]}
                        </td>
                        <td className="px-3 py-2">{outcomeBadge(r)}</td>
                        <td className="px-3 py-2">
                          {r.customer_name ?? (
                            <span className="text-muted-foreground">
                              {r.customer_email ? "—" : "Cliente no disponible"}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs">{r.customer_email ?? "—"}</td>
                        <td className="whitespace-nowrap px-3 py-2 font-medium">
                          {r.amount != null ? formatStripeAmount(r.amount, r.currency) : "—"}
                          {r.amount_refunded ? (
                            <span className="ml-1 text-xs text-destructive">
                              −{formatStripeAmount(r.amount_refunded, r.currency)}
                            </span>
                          ) : null}
                        </td>
                        <td className="px-3 py-2 font-mono text-[11px]">
                          {r.payment_intent_id ?? r.charge_id ?? r.object_id ?? r.stripe_event_id}
                        </td>
                        <td className="px-3 py-2">
                          <span className={
                            r.env === "live"
                              ? "rounded bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-700"
                              : "rounded bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-700"
                          }>
                            {r.env}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          {r.processed_at ? (
                            <span className="inline-flex items-center gap-1 text-emerald-700">
                              <CheckCircle2 className="h-3.5 w-3.5" />
                              Procesado
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-amber-700">
                              <Clock className="h-3.5 w-3.5" />
                              Reservado
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">
                          <ChevronRight className="h-4 w-4" />
                        </td>
                      </tr>
                    ))}
                    {rows.length === 0 && !query.isFetching ? (
                      <tr>
                        <td colSpan={11} className="px-3 py-8 text-center text-muted-foreground">
                          Sin eventos para estos filtros.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
              {query.hasNextPage ? (
                <div className="flex justify-center border-t p-3">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => query.fetchNextPage()}
                    disabled={query.isFetchingNextPage}
                  >
                    {query.isFetchingNextPage ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : null}
                    Cargar más
                  </Button>
                </div>
              ) : null}
            </div>
          </>
        )}
      </div>

      <Sheet open={!!openId} onOpenChange={(o) => !o && setOpenId(null)}>
        <SheetContent side="right" className="w-full max-w-2xl overflow-y-auto sm:max-w-2xl">
          <SheetHeader>
            <SheetTitle className="font-mono text-sm break-all">{openId}</SheetTitle>
          </SheetHeader>

          {detail.isLoading ? (
            <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
            </div>
          ) : detail.error ? (
            <div className="mt-4 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
              {(detail.error as Error).message}
            </div>
          ) : detail.data ? (
            <div className="mt-4 space-y-6 text-sm">
              <section>
                <h3 className="mb-2 font-semibold">Evento</h3>
                {detail.data.event ? (
                  <div className="grid grid-cols-2 gap-2 rounded-md border bg-muted/30 p-3">
                    <div><span className="text-muted-foreground">Tipo:</span> {detail.data.event.event_type ?? "—"}</div>
                    <div>
                      <span className="text-muted-foreground">Categoría:</span>{" "}
                      {CATEGORY_LABELS[(detail.data.event.category ?? "other") as StripeEventCategory]}
                    </div>
                    <div><span className="text-muted-foreground">Env:</span> {detail.data.event.env}</div>
                    <div><span className="text-muted-foreground">Recibido:</span> {fmtDate(detail.data.event.received_at)}</div>
                    <div>
                      <span className="text-muted-foreground">Procesado:</span>{" "}
                      {detail.data.event.processed_at ? fmtDate(detail.data.event.processed_at) : "—"}
                    </div>
                    <div>
                      <span className="text-muted-foreground">Creado en Stripe:</span>{" "}
                      {detail.data.event.event_created_at ? fmtDate(detail.data.event.event_created_at) : "—"}
                    </div>
                  </div>
                ) : (
                  <p className="text-muted-foreground">No encontrado.</p>
                )}
              </section>

              <section>
                <h3 className="mb-2 font-semibold">Datos de Stripe</h3>
                {detail.data.event ? (
                  <>
                    <div className="grid grid-cols-2 gap-2 rounded-md border bg-muted/30 p-3 text-xs">
                      {(() => {
                        const e = detail.data.event!;
                        const items: Array<[string, string]> = [
                          ["Objeto", e.object_type ?? "—"],
                          ["ID objeto", e.object_id ?? "—"],
                          ["Cliente", e.customer_name ?? "Cliente no disponible"],
                          ["Email", e.customer_email ?? "—"],
                          ["Customer ID", e.customer_id ?? "—"],
                          ["PaymentIntent", e.payment_intent_id ?? "—"],
                          ["Charge", e.charge_id ?? "—"],
                          ["Invoice", e.invoice_id ?? "—"],
                          ["Checkout Session", e.checkout_session_id ?? "—"],
                          ["Suscripción", e.subscription_id ?? "—"],
                          ["Reembolso", e.refund_id ?? "—"],
                          ["Disputa", e.dispute_id ?? "—"],
                          ["Monto", e.amount != null ? formatStripeAmount(e.amount, e.currency) : "—"],
                          ["Monto recibido", e.amount_received != null ? formatStripeAmount(e.amount_received, e.currency) : "—"],
                          ["Reembolsado", e.amount_refunded != null ? formatStripeAmount(e.amount_refunded, e.currency) : "—"],
                          ["Moneda", (e.currency ?? "—").toUpperCase()],
                          ["Estado", e.status ?? "—"],
                          ["Resultado", e.outcome ?? "—"],
                          ["Descripción", e.description ?? "—"],
                          ["Livemode", e.livemode == null ? "—" : e.livemode ? "Sí" : "No"],
                        ];
                        return items.map(([k, v]) => (
                          <div key={k}>
                            <span className="text-muted-foreground">{k}:</span>{" "}
                            <span className="break-all">{v}</span>
                          </div>
                        ));
                      })()}
                    </div>
                    {detail.data.payloadJson ? (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-xs text-muted-foreground">
                          Ver payload completo del webhook
                        </summary>
                        <pre className="mt-2 max-h-80 overflow-auto rounded bg-background p-2 text-[11px]">
                          {detail.data.payloadJson}
                        </pre>
                      </details>
                    ) : (
                      <p className="mt-2 text-xs text-muted-foreground">
                        Este evento se registró antes de guardar el payload completo.
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-muted-foreground">Sin datos de Stripe para este evento.</p>
                )}
              </section>

              <section>
                <h3 className="mb-2 font-semibold">
                  Maya events relacionados ({detail.data.mayaEvents.length})
                </h3>
                {detail.data.mayaEvents.length === 0 ? (
                  <p className="text-muted-foreground">Sin registros.</p>
                ) : (
                  <ul className="space-y-2">
                    {detail.data.mayaEvents.map((ev) => (
                      <li key={ev.id} className="rounded-md border bg-muted/30 p-3">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{ev.event_type}</span>
                          <span className="text-xs text-muted-foreground">{fmtDate(ev.created_at)}</span>
                        </div>
                        <div className="mt-1 text-xs text-muted-foreground">
                          {ev.destination ?? "—"}
                          {ev.amount_cents != null
                            ? ` · ${(ev.amount_cents / 100).toFixed(2)} ${(ev.currency ?? "").toUpperCase()}`
                            : ""}
                        </div>
                        {ev.metadata_json ? (
                          <pre className="mt-2 max-h-40 overflow-auto rounded bg-background p-2 text-xs">
                            {ev.metadata_json}
                          </pre>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section>
                <h3 className="mb-2 font-semibold">
                  Maya purchases relacionadas ({detail.data.mayaPurchases.length})
                </h3>
                {detail.data.mayaPurchases.length === 0 ? (
                  <p className="text-muted-foreground">Sin registros.</p>
                ) : (
                  <ul className="space-y-2">
                    {detail.data.mayaPurchases.map((p) => (
                      <li key={p.id} className="rounded-md border bg-muted/30 p-3 text-xs">
                        <div className="flex items-center justify-between">
                          <span className="font-mono">{p.customer_email ?? "—"}</span>
                          <span className="rounded bg-background px-2 py-0.5">{p.status}</span>
                        </div>
                        <div className="mt-1 text-muted-foreground">
                          {p.product_id} · {p.tour_slug ?? p.destination_key ?? "—"} ·{" "}
                          {p.amount != null ? `${(p.amount / 100).toFixed(2)} ${(p.currency ?? "").toUpperCase()}` : "—"} · {p.environment}
                        </div>
                        <div className="mt-1 font-mono text-[10px] text-muted-foreground">
                          {p.stripe_session_id ?? p.stripe_payment_intent_id ?? ""}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}
